import React, { useEffect, useMemo, useState } from 'react';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { usePanels } from '../../shared/hooks/use-panels';
import { useAlphaFalloff } from '../hooks/use-alpha-falloff';
import { useChromaKeySync } from '../hooks/use-chroma-key';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer } from '../../selection/hooks/use-selected-layer';
import type { LayerId } from '../../model/types';
import { useMedia } from '../../media';
import { ChromaKeyControls } from './chroma-key-controls';
import { AlphaFalloffControls } from './alpha-falloff-controls';
import { SectionTitle } from './shared';
import { ChromaKeyParams, DEFAULT_CHROMA_KEY_PARAMS } from '../constants/chroma-key-shader';
import { classifySubject } from '../utils/classify-subject';
import { inspectorPanelStyles } from '../../inspector/styles';

export const EffectsPanel: React.FC = () => {
  const { canvas, selectedObject, transaction } = useEditorCanvas();
  const { getVideo } = useMedia();
  const { isOpen } = usePanels();
  const { activeResolution } = useResolutions();
  const designWidth = activeResolution.canvasWidth;
  const designHeight = activeResolution.canvasHeight;

  const { setSubjectEffect } = useAlphaFalloff(canvas, designWidth, designHeight, transaction);
  const { setVideoChroma } = useChromaKeySync(canvas);
  const dispatch = usePieceDispatch();
  const layer = useSelectedLayer();

  const modelChroma = layer?.kind === 'video' ? layer.content.chromaKey : undefined;
  const modelAlpha =
    layer && layer.kind !== 'audio' && 'alphaFalloff' in layer.content ? layer.content.alphaFalloff : undefined;

  const subject = useMemo(() => classifySubject(selectedObject, getVideo), [selectedObject, getVideo]);

  const [chromaEnabled, setChromaEnabled] = useState(false);
  const [chromaParams, setChromaParams] = useState<ChromaKeyParams>(DEFAULT_CHROMA_KEY_PARAMS);

  useEffect(() => {
    setChromaEnabled(!!modelChroma);
    setChromaParams(modelChroma ?? DEFAULT_CHROMA_KEY_PARAMS);
  }, [subject, modelChroma]);

  // Live preview
  useEffect(() => {
    if (subject.kind !== 'video') return;
    const timer = window.setTimeout(() => {
      setVideoChroma(subject.obj, chromaEnabled, chromaParams);
    }, 30);
    return () => window.clearTimeout(timer);
  }, [subject, chromaEnabled, chromaParams, setVideoChroma]);

  const commitChroma = (enabled: boolean, params: ChromaKeyParams) => {
    if (subject.kind !== 'video') return;
    setVideoChroma(subject.obj, enabled, params);
    const next = enabled ? params : undefined;
    if (JSON.stringify(next) === JSON.stringify(modelChroma)) return;
    const id = subject.obj.layerId as LayerId | undefined;
    if (id) dispatch({ type: 'patchLayerContent', id, patch: { chromaKey: next } });
  };

  if (!isOpen('effects') || subject.kind === 'none') return null;

  return (
    <div data-panel-id='effects' style={inspectorPanelStyles}>
      {subject.kind === 'video' && (
        <>
          <SectionTitle>Chroma key</SectionTitle>
          <ChromaKeyControls
            enabled={chromaEnabled}
            onToggle={setChromaEnabled}
            params={chromaParams}
            onParamsChange={setChromaParams}
            onCommit={commitChroma}
          />
        </>
      )}

      {subject.kind === 'falloff' && (
        <>
          <SectionTitle>Alpha falloff</SectionTitle>
          <AlphaFalloffControls subject={subject.obj} modelParams={modelAlpha} setSubjectEffect={setSubjectEffect} />
        </>
      )}
    </div>
  );
};
